import SisiMarkdown from "./SisiMarkdown";
import {
  ArrowRight,
  BarChart3,
  Building2,
  Loader2,
  MessageSquareText,
  Send,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  api,
} from "@/lib/api";

import SisiAvatar from "./SisiAvatar";
import ScreenOverlay from "../common/ScreenOverlay";


const QUICK_ACTIONS = [
  {
    id:
      "portfolio",

    label:
      "Analisar minha carteira",

    description:
      "Visão consolidada das empresas monitoradas.",

    icon:
      Building2,
  },

  {
    id:
      "attention",

    label:
      "O que precisa da minha atenção?",

    description:
      "Prioridades, riscos e pendências fiscais.",

    icon:
      ShieldCheck,
  },

  {
    id:
      "messages",

    label:
      "Ver mensagens importantes",

    description:
      "Acompanhe comunicações e alertas fiscais.",

    icon:
      MessageSquareText,
  },
];


const normalizeArray = (
  value
) => {
  if (
    Array.isArray(
      value
    )
  ) {
    return value;
  }

  if (
    Array.isArray(
      value?.items
    )
  ) {
    return value.items;
  }

  if (
    Array.isArray(
      value?.companies
    )
  ) {
    return value.companies;
  }

  if (
    Array.isArray(
      value?.prioridades
    )
  ) {
    return value.prioridades;
  }

  if (
    Array.isArray(
      value?.rows
    )
  ) {
    return value.rows;
  }

  if (
    Array.isArray(
      value?.data
    )
  ) {
    return value.data;
  }

  return [];
};


const normalizeText = (
  value
) =>
  String(
    value || ""
  )
    .trim()
    .toLowerCase()
    .normalize(
      "NFD"
    )
    .replace(
      /[\u0300-\u036f]/g,
      ""
    );


const onlyDigits = (
  value
) =>
  String(
    value || ""
  ).replace(
    /\D/g,
    ""
  );


const companyName = (
  company
) =>
  company?.razao_social ||
  company?.nome_fantasia ||
  company?.name ||
  company?.company_name ||
  "Empresa";


const companyCnpj = (
  company
) =>
  company?.cnpj ||
  company?.document ||
  "";


const companyId = (
  company
) =>
  company?.id ||
  company?._id ||
  company?.company_id ||
  null;


const priorityCompanyName = (
  item
) =>
  item?.company_name ||
  item?.razao_social ||
  item?.empresa ||
  item?.company?.name ||
  item?.company?.razao_social ||
  "Empresa da carteira";


const priorityTitle = (
  item
) =>
  item?.title ||
  item?.titulo ||
  item?.label ||
  item?.tipo ||
  item?.category ||
  "Ponto de atenção fiscal";


const priorityDescription = (
  item
) =>
  item?.description ||
  item?.descricao ||
  item?.detail ||
  item?.reason ||
  item?.message ||
  "";


const extractMessagesCount = (
  data
) => {
  const candidates = [
    data?.mailbox?.unread,
    data?.mailbox?.unread_count,
    data?.messages?.unread,
    data?.messages?.unread_count,
    data?.mensagens?.nao_lidas,
    data?.unread_messages,
  ];

  for (
    const value of candidates
  ) {
    if (
      value !== undefined &&
      value !== null &&
      value !== ""
    ) {
      const number =
        Number(
          value
        );

      if (
        Number.isFinite(
          number
        )
      ) {
        return number;
      }
    }
  }

  return null;
};


export default function SisiChatPanel({
  open,
  onClose,
}) {
  const navigate =
    useNavigate();


  const [
    loadingContext,
    setLoadingContext,
  ] = useState(
    false
  );


  const [
    sending,
    setSending,
  ] = useState(
    false
  );


  const [
    input,
    setInput,
  ] = useState(
    ""
  );


  const [
    activeCompany,
    setActiveCompany,
  ] = useState(
    null
  );


  const [
    companies,
    setCompanies,
  ] = useState(
    []
  );


  const [
    priorities,
    setPriorities,
  ] = useState(
    []
  );


  const [
    operational,
    setOperational,
  ] = useState(
    null
  );


  const [
    messages,
    setMessages,
  ] = useState(
    [
      {
        id:
          "welcome",

        role:
          "assistant",

        content:
          "Olá! Eu sou a Sisi, sua Analista Fiscal Inteligente. Posso analisar sua carteira, mostrar prioridades e analisar empresas usando os dados disponíveis na Simpler.",
      },
    ]
  );

  const resetConversation =
    () => {
      setMessages(
        [
          {
            id:
              "welcome",

            role:
              "assistant",

            content:
              "Olá! Eu sou a Sisi, sua Analista Fiscal Inteligente. Posso analisar sua carteira, mostrar prioridades e analisar empresas usando os dados disponíveis na Simpler.",
          },
        ]
      );

      setInput(
        ""
      );

      setActiveCompany(
        null
      );
    };



  const scrollRef =
    useRef(
      null
    );


  const loadContext =
    useCallback(
      async () => {
        try {
          setLoadingContext(
            true
          );


          const [
            companiesResponse,
            prioritiesResponse,
            operationalResponse,
          ] =
            await Promise.allSettled(
              [
                api.get(
                  "/companies",
                  {
                    params: {
                      limit: 200,
                    },
                  }
                ),

                api.get(
                  "/bi/prioridades"
                ),

                api.get(
                  "/bi/operacional"
                ),
              ]
            );


          if (
            companiesResponse.status ===
            "fulfilled"
          ) {
            const data =
              companiesResponse
                .value
                ?.data;

            setCompanies(
              normalizeArray(
                data?.items ||
                data?.companies ||
                data?.rows ||
                data
              )
            );
          }


          if (
            prioritiesResponse.status ===
            "fulfilled"
          ) {
            const data =
              prioritiesResponse
                .value
                ?.data;

            setPriorities(
              normalizeArray(
                data?.items ||
                data?.prioridades ||
                data
              )
            );
          }


          if (
            operationalResponse.status ===
            "fulfilled"
          ) {
            setOperational(
              operationalResponse
                .value
                ?.data ||
              null
            );
          }

        } catch (
          error
        ) {
          console.error(
            "Sisi context:",
            error
          );

        } finally {
          setLoadingContext(
            false
          );
        }
      },
      []
    );


  useEffect(
    () => {
      if (
        open
      ) {
        loadContext();
      }
    },
    [
      open,
      loadContext,
    ]
  );


  useEffect(
    () => {
      const node =
        scrollRef.current;

      if (
        node
      ) {
        node.scrollTop =
          node.scrollHeight;
      }
    },
    [
      messages,
      sending,
    ]
  );


  const addAssistantMessage =
    (
      content,
      options = {}
    ) => {
      setMessages(
        current => [
          ...current,
          {
            id:
              `${Date.now()}-${Math.random()}`,

            role:
              "assistant",

            content,

            ...options,
          },
        ]
      );
    };


  const addUserMessage =
    (
      content
    ) => {
      setMessages(
        current => [
          ...current,
          {
            id:
              `${Date.now()}-${Math.random()}`,

            role:
              "user",

            content,
          },
        ]
      );
    };


  const contextSummary =
    useMemo(
      () => ({
        total_empresas:
          companies.length,

        prioridades:
          priorities
            .slice(
              0,
              12
            )
            .map(
              item => ({
                empresa:
                  priorityCompanyName(
                    item
                  ),

                titulo:
                  priorityTitle(
                    item
                  ),

                descricao:
                  priorityDescription(
                    item
                  ),

                prioridade:
                  item?.priority ||
                  item?.prioridade ||
                  item?.severity ||
                  item?.nivel ||
                  null,
              })
            ),

        mensagens_nao_lidas:
          extractMessagesCount(
            operational
          ),
      }),
      [
        companies,
        priorities,
        operational,
      ]
    );


  const findCompany =
    (
      query
    ) => {
      const normalized =
        normalizeText(
          query
        );

      const digits =
        onlyDigits(
          query
        );


      if (
        digits.length >= 8
      ) {
        const byCnpj =
          companies.find(
            company =>
              onlyDigits(
                companyCnpj(
                  company
                )
              ).includes(
                digits
              ) ||
              digits.includes(
                onlyDigits(
                  companyCnpj(
                    company
                  )
                )
              )
          );

        if (
          byCnpj
        ) {
          return byCnpj;
        }
      }


      const candidates =
        companies.filter(
          company => {
            const name =
              normalizeText(
                companyName(
                  company
                )
              );

            const fantasy =
              normalizeText(
                company
                  ?.nome_fantasia
              );

            return (
              (
                name.length > 3 &&
                normalized.includes(
                  name
                )
              ) ||
              (
                fantasy.length > 3 &&
                normalized.includes(
                  fantasy
                )
              ) ||
              (
                normalized.length > 4 &&
                name.includes(
                  normalized
                )
              )
            );
          }
        );


      return (
        candidates[0] ||
        null
      );
    };


  const buildPortfolioFallback =
    () => {
      const totalCompanies =
        companies.length;


      const totalPriorities =
        priorities.length;


      const unreadMessages =
        extractMessagesCount(
          operational
        );


      const topPriorities =
        priorities
          .slice(
            0,
            5
          )
          .map(
            (
              item,
              index
            ) =>
              `${index + 1}. ${priorityCompanyName(
                item
              )} — ${priorityTitle(
                item
              )}`
          );


      const lines = [
        `Analisei os dados atualmente disponíveis da sua carteira na Simpler.`,
        "",
        totalCompanies > 0
          ? `• Empresas monitoradas: ${totalCompanies}`
          : null,

        totalPriorities > 0
          ? `• Prioridades identificadas: ${totalPriorities}`
          : `• Nenhuma prioridade foi retornada no momento.`,

        unreadMessages !== null
          ? `• Mensagens não lidas: ${unreadMessages}`
          : null,
      ].filter(
        Boolean
      );


      if (
        topPriorities.length > 0
      ) {
        lines.push(
          "",
          "Principais pontos que precisam de atenção:",
          "",
          ...topPriorities
        );
      }


      if (
        totalPriorities > 0
      ) {
        lines.push(
          "",
          "Minha recomendação é começar pelas prioridades de maior criticidade e acessar cada módulo relacionado para verificar os detalhes."
        );
      }


      return lines.join(
        "\n"
      );
    };


  const analyzePortfolio =
    async () => {
      const fallback =
        buildPortfolioFallback();


      const prompt =
        `Você é a Sisi, Analista Fiscal Inteligente da Simpler.

Analise SOMENTE os dados reais resumidos abaixo.
Não invente empresas, valores, pendências ou situações fiscais.
Se um dado não estiver presente, diga que ele não está disponível.

DADOS DA CARTEIRA:
${JSON.stringify(
  contextSummary,
  null,
  2
)}

Responda em português, de forma objetiva e executiva, contendo:
1. visão geral da carteira;
2. principais prioridades;
3. empresas que merecem atenção;
4. próximos passos recomendados.`;

      try {
        if (shouldClearActiveCompany(question)) {
          setActiveCompany(null);
        }

        const response =
          await api.post(
            "/ai/ask",
            {
              prompt,
            }
          );


        const content =
          response
            ?.data
            ?.content;


        return (
          content?.trim()
            ? content
            : fallback
        );

      } catch (
        error
      ) {
        console.warn(
          "Sisi: provedor de IA indisponível; usando análise determinística.",
          error
        );


        /*
         * IMPORTANTE:
         * não propaga erro técnico do provedor.
         * A análise continua utilizando os dados reais
         * já carregados da carteira.
         */
        return fallback;
      }
    };


  const analyzeCompany =
    async (
      company
    ) => {
      const id =
        companyId(
          company
        );


      const name =
        companyName(
          company
        );


      const cnpj =
        companyCnpj(
          company
        );


      if (
        !id
      ) {
        return (
          `Localizei ${name}, mas o identificador necessário para executar a análise detalhada não está disponível.`
        );
      }


      try {
        const response =
          await api.post(
            `/ai/analyze-company/${id}`
          );


        const content =
          response
            ?.data
            ?.content;


        if (
          content?.trim()
        ) {
          return content;
        }

      } catch (
        error
      ) {
        console.warn(
          "Sisi: análise IA da empresa indisponível.",
          error
        );
      }


      /*
       * Não inventar diagnóstico quando o provedor
       * externo estiver indisponível.
       */
      return (
        `Localizei a empresa ${name}${
          cnpj
            ? ` (${cnpj})`
            : ""
        } na sua carteira.\n\n` +
        `A análise inteligente detalhada está temporariamente indisponível, mas os dados da empresa continuam acessíveis na Simpler. ` +
        `Você pode consultar o Health Score, Diagnóstico Fiscal e demais módulos sincronizados para verificar a situação atual.`
      );
    };


  const answerAttention =
    () => {
      if (
        priorities.length === 0
      ) {
        return {
          text:
            "Não encontrei prioridades disponíveis para exibição neste momento.",

          actionPath:
            "/app/prioridades",

          actionLabel:
            "Abrir Prioridades",
        };
      }


      const top =
        priorities
          .slice(
            0,
            8
          )
          .map(
            (
              item,
              index
            ) =>
              `${index + 1}. ${priorityCompanyName(
                item
              )} — ${priorityTitle(
                item
              )}${
                priorityDescription(
                  item
                )
                  ? `: ${priorityDescription(
                      item
                    )}`
                  : ""
              }`
          )
          .join(
            "\n"
          );


      return {
        text:
          `Encontrei ${priorities.length} prioridade(s) na carteira. As principais são:\n\n${top}`,

        actionPath:
          "/app/prioridades",

        actionLabel:
          "Ver todas as prioridades",
      };
    };


  const answerCompanies =
    () => {
      if (
        companies.length === 0
      ) {
        return (
          "Não consegui carregar a lista de empresas neste momento."
        );
      }


      const list =
        companies
          .slice(
            0,
            15
          )
          .map(
            (
              company,
              index
            ) => {
              const cnpj =
                companyCnpj(
                  company
                );

              return (
                `${index + 1}. ${companyName(
                  company
                )}${
                  cnpj
                    ? ` — ${cnpj}`
                    : ""
                }`
              );
            }
          )
          .join(
            "\n"
          );


      return (
        `Sua carteira possui ${companies.length} empresa(s) carregada(s).\n\n${list}${
          companies.length > 15
            ? `\n\nMostrando as primeiras 15 de ${companies.length}.`
            : ""
        }`
      );
    };


  const answerMessages =
    () => {
      const unread =
        extractMessagesCount(
          operational
        );


      return {
        text:
          unread === null
            ? "Os dados consolidados de mensagens não trouxeram uma contagem disponível. Posso abrir o módulo de Mensagens para você consultar as comunicações fiscais."
            : unread === 0
            ? "Não há mensagens não lidas indicadas no resumo operacional neste momento."
            : `Existem ${unread} mensagem(ns) não lida(s) indicadas no resumo operacional.`,

        actionPath:
          "/app/mensagens",

        actionLabel:
          "Abrir Mensagens",
      };
    };



  // SISI_GENERAL_SCOPE_V1
  const isGeneralScopeRequest = (text) => {
    const normalized = String(text || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    const patterns = [
      "geral",
      "quero geral",
      "quero de todas",
      "de todas",
      "todas",
      "todas as empresas",
      "todas empresas",
      "todos os clientes",
      "todos clientes",
      "quero de todos",
      "de todos",
      "carteira toda",
      "toda a carteira",
      "carteira inteira",
      "visao geral",
      "visao da carteira",
      "nao so da",
      "nao so dessa",
      "nao so desse",
      "nao apenas da",
      "nao somente da",
      "sem focar nessa empresa",
      "sem focar na empresa",
    ];

    return patterns.some(
      pattern => normalized.includes(pattern)
    );
  };


  // SISI_AUTO_PORTFOLIO_SCOPE_V1
  const isPortfolioScopeRequest = (text) => {
    const normalized = String(text || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");


    const portfolioPatterns = [
      "quem esta sem",
      "quem ta sem",
      "quem nao entregou",
      "quem nao transmitiu",
      "quais empresas",
      "quais clientes",
      "quais estao",
      "quais estão",
      "quem tem",
      "quem esta com",
      "quem está com",
      "quais tem",
      "quais têm",
      "no geral",
      "é no geral",
      "e no geral",
      "geral mesmo",
      "da carteira",
      "na carteira",
      "carteira toda",
      "toda a carteira",
    ];


    return portfolioPatterns.some(
      pattern =>
        normalized.includes(
          pattern
        )
    );
  };


  const shouldClearActiveCompany = (text) =>
    isGeneralScopeRequest(text) ||
    isPortfolioScopeRequest(text);




  // SISI_API_SMART_FALLBACK_V1
  const getSmartApiFallback = (
    question,
    error = null
  ) => {
    const normalized =
      normalizeText(
        question
      );


    // --------------------------------------------------------
    // EFD-CONTRIBUICOES
    //
    // Isso só é utilizado se a chamada HTTP falhar.
    // O backend continua sendo a fonte principal.
    // --------------------------------------------------------

    const isEfdContribuicoes =
      normalized.includes(
        "efd contribuicao"
      ) ||
      normalized.includes(
        "efd contribuicoes"
      ) ||
      normalized.includes(
        "sped contribuicao"
      ) ||
      normalized.includes(
        "sped contribuicoes"
      );


    if (isEfdContribuicoes) {

      const companyName =
        activeCompany
          ?.razao_social ||
        activeCompany
          ?.nome_fantasia ||
        null;


      const companyPart =
        companyName
          ? (
              ` da ${companyName}`
            )
          : "";


      const nextStep =
        companyName
          ? (
              `Se quiser, posso verificar se ${companyName} ` +
              "possui alguma ausência ou pendência relacionada " +
              "à EFD nos dados disponíveis."
            )
          : (
              "Se quiser, posso verificar quais empresas da " +
              "carteira possuem ausência ou pendência relacionada " +
              "à EFD nos dados disponíveis."
            );


      return {
        content:
          "A Simpler não possui hoje um módulo operacional " +
          "dedicado à EFD-Contribuições.\n\n" +
          "Eu consigo verificar se existem ausências ou pendências " +
          `relacionadas à EFD${companyPart} nos dados fiscais ` +
          "sincronizados na Simpler, mas não vou afirmar que consultei " +
          "ou transmiti a escrituração completa porque essa " +
          "funcionalidade não está disponível como módulo próprio.\n\n" +
          nextStep,

        actionPath:
          "/app/obrigacoes",

        actionLabel:
          "Abrir Obrigações",

        intent:
          "product_capability",

        company:
          activeCompany ||
          null,
      };
    }


    // --------------------------------------------------------
    // ERRO HTTP COM DETALHE DO BACKEND
    // --------------------------------------------------------



    // SISI_SMART_FALLBACK_HTTP_DIAGNOSTIC_V1
    //
    // Diagnóstico temporário.
    // Se chegou aqui, a chamada /ai/sisi falhou.
    // Mostramos o status real antes dos fallbacks por módulo.
    //

    if (error) {

      const status =
        error
          ?.response
          ?.status ||
        null;


      const backendData =
        error
          ?.response
          ?.data;


      const backendDetail =
        typeof backendData?.detail === "string"
          ? backendData.detail
          : (
              backendData?.detail
                ? JSON.stringify(
                    backendData.detail
                  )
                : (
                    typeof backendData === "string"
                      ? backendData
                      : ""
                  )
            );


      console.error(
        "Sisi API diagnostic:",
        {
          status,

          detail:
            backendDetail,

          data:
            backendData,

          message:
            error?.message,

          url:
            error
              ?.config
              ?.url,

          baseURL:
            error
              ?.config
              ?.baseURL,

          method:
            error
              ?.config
              ?.method,

          question,
        }
      );


      return {
        content:
          status
            ? (
                `DEBUG SISI — HTTP ${status}` +
                (
                  backendDetail
                    ? `\n\n${backendDetail}`
                    : ""
                )
              )
            : (
                "DEBUG SISI — falha de rede/API sem status HTTP." +
                (
                  error?.message
                    ? `\n\n${error.message}`
                    : ""
                )
              ),

        intent:
          "debug_http",
      };
    }


    // SISI_DAILY_SMART_FALLBACK_V2
    //
    // Se a API estiver momentaneamente indisponível,
    // não responder com erro genérico para módulos
    // conhecidos da Simpler.
    //

    const moduleFallbacks = [
      {
        match: [
          "dctfweb",
          "dctf web",
          "dtcfweb",
        ],

        content:
          "Posso te ajudar com a DCTFWeb usando os dados disponíveis na Simpler. " +
          "Para consultar a carteira completa, abra o módulo DCTFWeb.",

        actionPath:
          "/app/dctfweb",

        actionLabel:
          "Abrir DCTFWeb",
      },

      {
        match: [
          " mit ",
          "sem mit",
          "entregou mit",
        ],

        content:
          "Posso verificar a situação do MIT com base nos dados disponíveis na Simpler. " +
          "Para visualizar a carteira completa, abra o módulo MIT.",

        actionPath:
          "/app/dashboard-federal/mit",

        actionLabel:
          "Abrir MIT",
      },

      {
        match: [
          "pgdas",
        ],

        content:
          "Posso te ajudar com o acompanhamento do PGDAS-D disponível na Simpler.",

        actionPath:
          "/app/dashboard-federal/pgdas",

        actionLabel:
          "Abrir PGDAS",
      },

      {
        match: [
          "divida ativa",
          "pgfn",
        ],

        content:
          "Posso trabalhar com os registros de Dívida Ativa / PGFN disponíveis na Simpler.",

        actionPath:
          "/app/dashboard-federal/divida-ativa",

        actionLabel:
          "Abrir Dívida Ativa",
      },

      {
        match: [
          "parcelamento",
          "parcelamentos",
          "parcela atrasada",
        ],

        content:
          "Posso te ajudar com os parcelamentos registrados na Simpler.",

        actionPath:
          "/app/parcelamentos",

        actionLabel:
          "Abrir Parcelamentos",
      },

      {
        match: [
          "certidao",
          "certidoes",
          "cndt",
        ],

        content:
          "Posso consultar as certidões disponíveis na Simpler.",

        actionPath:
          "/app/certidoes",

        actionLabel:
          "Abrir Certidões",
      },

      {
        match: [
          "mensagem",
          "mensagens",
          "caixa postal",
          "intimacao",
        ],

        content:
          "Posso consultar as mensagens e comunicações disponíveis na Simpler.",

        actionPath:
          "/app/mensagens",

        actionLabel:
          "Abrir Mensagens",
      },

      {
        match: [
          "obrigacao",
          "obrigacoes",
          "declaracao pendente",
          "declaracoes pendentes",
        ],

        content:
          "Posso te ajudar a acompanhar obrigações e ausências registradas na Simpler.",

        actionPath:
          "/app/obrigacoes",

        actionLabel:
          "Abrir Obrigações",
      },
    ];


    for (
      const moduleFallback
      of moduleFallbacks
    ) {

      const matched =
        moduleFallback
          .match
          .some(
            term =>
              ` ${normalized} `
                .includes(
                  term
                )
          );


      if (matched) {

        return {
          content:
            moduleFallback.content,

          actionPath:
            moduleFallback.actionPath,

          actionLabel:
            moduleFallback.actionLabel,

          intent:
            "product_capability",
        };
      }
    }


    const backendDetail =
      error
        ?.response
        ?.data
        ?.detail;


    if (
      typeof backendDetail === "string" &&
      backendDetail.trim()
    ) {
      return {
        content:
          "Não consegui concluir essa consulta agora. " +
          backendDetail,
      };
    }


    return {
      content:
        "Não consegui concluir essa consulta agora. " +
        "Tente novamente em alguns instantes ou reformule a pergunta.",
    };
  };


  const askSisi =
    async (
      question
    ) => {
      const normalized =
        normalizeText(
          question
        );


      if (
        normalized.includes(
          "listar empresas"
        ) ||
        normalized.includes(
          "mostrar empresas"
        ) ||
        normalized ===
          "empresas" ||
        normalized.includes(
          "quais empresas tenho"
        )
      ) {
        return {
          content:
            answerCompanies(),
        };
      }


      if (
        normalized.includes(
          "prioridade"
        ) ||
        normalized.includes(
          "precisa da minha atencao"
        ) ||
        normalized.includes(
          "precisam de atencao"
        )
      ) {
        const answer =
          answerAttention();

        return {
          content:
            answer.text,

          actionPath:
            answer.actionPath,

          actionLabel:
            answer.actionLabel,
        };
      }


      if (
        normalized.includes(
          "mensagem"
        )
      ) {
        const answer =
          answerMessages();

        return {
          content:
            answer.text,

          actionPath:
            answer.actionPath,

          actionLabel:
            answer.actionLabel,
        };
      }
if (
        normalized.includes(
          "analise minha carteira"
        ) ||
        normalized.includes(
          "analisar minha carteira"
        ) ||
        normalized.includes(
          "analise a carteira"
        )
      ) {
        return {
          content:
            await analyzePortfolio(),
        };
      }


      try {
        const response =
          await api.post(
            "/ai/sisi",
            {
              message:
                question,

              history:
                messages
                  .slice(
                    -8
                  )
                  .map(
                    item => ({
                      role:
                        item.role,

                      content:
                        item.content,
                    })
                  ),

              company_id:
                shouldClearActiveCompany(question)
                  ? null
                  : (
                      activeCompany
                        ?.id ||
                      null
                    ),
            }
          );


        const returnedCompany =
          response
            ?.data
            ?.company ||
          null;


        if (
          returnedCompany
            ?.id
        ) {
          setActiveCompany(
            returnedCompany
          );
        }


        return {
          content:
            response
              ?.data
              ?.content ||
            "Não foi possível obter uma resposta neste momento.",

          actionPath:
            response
              ?.data
              ?.action_path ||
            null,

          actionLabel:
            response
              ?.data
              ?.action_label ||
            null,


          actions:
            Array.isArray(
              response
                ?.data
                ?.actions
            )
              ? response.data.actions
              : [],

          company:
            returnedCompany,

          intent:
            response
              ?.data
              ?.intent ||
            null,

          candidates:
            Array.isArray(
              response
                ?.data
                ?.candidates
            )
              ? response.data.candidates
              : [],
        };

      } catch (
        error
      ) {
        console.error(
          "Sisi Orchestrator HTTP error:",
          {
            message:
              error?.message,

            status:
              error
                ?.response
                ?.status,

            statusText:
              error
                ?.response
                ?.statusText,

            data:
              error
                ?.response
                ?.data,

            url:
              error
                ?.config
                ?.url,

            baseURL:
              error
                ?.config
                ?.baseURL,

            method:
              error
                ?.config
                ?.method,

            question,
          }
        );


        return getSmartApiFallback(
          question,
          error
        );
      }
    };


  const handleSend =
    async (
      forcedText = null
    ) => {
      const text =
        String(
          forcedText ??
          input
        ).trim();


      if (
        !text ||
        sending
      ) {
        return;
      }


      addUserMessage(
        text
      );

      setInput(
        ""
      );


      try {
        setSending(
          true
        );


        const result =
          await askSisi(
            text
          );


        addAssistantMessage(
          result.content,
          {
            actionPath:
              result.actionPath,

            actionLabel:
              result.actionLabel,


            actions:
              result.actions ||
              [],

            candidates:
              result.candidates ||
              [],

            originalQuestion:
              text,
          }
        );

      } catch (
        error
      ) {
        console.error(
          "Sisi ask:",
          error
        );


        addAssistantMessage(
          "Não consegui concluir essa análise inteligente agora. Os dados sincronizados continuam disponíveis — tente listar empresas, consultar prioridades ou verificar mensagens."
        );

      } finally {
        setSending(
          false
        );
      }
    };


  const handleCandidateSelect =
    async (
      candidate,
      originalQuestion
    ) => {
      if (
        !candidate
          ?.id
      ) {
        return;
      }


      setActiveCompany(
        candidate
      );


      const name =
        candidate
          ?.razao_social ||
        candidate
          ?.nome_fantasia ||
        "empresa selecionada";


      addUserMessage(
        name
      );


      try {
        setSending(
          true
        );


        const response =
          await api.post(
            "/ai/sisi",
            {
              message:
                originalQuestion ||
                "Continuar análise da empresa selecionada",

              history:
                messages
                  .slice(
                    -8
                  )
                  .map(
                    item => ({
                      role:
                        item.role,

                      content:
                        item.content,
                    })
                  ),

              company_id:
                candidate.id,
            }
          );


        const returnedCompany =
          response
            ?.data
            ?.company ||
          candidate;


        if (
          returnedCompany
            ?.id
        ) {
          setActiveCompany(
            returnedCompany
          );
        }


        addAssistantMessage(
          response
            ?.data
            ?.content ||
          "Não foi possível concluir a consulta da empresa selecionada.",

          {
            actionPath:
              response
                ?.data
                ?.action_path ||
              null,

            actionLabel:
              response
                ?.data
                ?.action_label ||
              null,

            candidates:
              Array.isArray(
                response
                  ?.data
                  ?.candidates
              )
                ? response.data.candidates
                : [],
          }
        );

      } catch (
        error
      ) {
        console.error(
          "Sisi candidate:",
          error
        );


        addAssistantMessage(
          "Não consegui concluir a consulta dessa empresa agora."
        );

      } finally {
        setSending(
          false
        );
      }
    };



  // SISI_DOCUMENT_ACTION_HANDLER_V1
  //
  // Páginas internas /app/... continuam usando React Router.
  //
  // Documentos /api/... são buscados pela instância Axios
  // autenticada e abertos como Blob em nova aba.
  //
  // Assim não enviamos endpoints de PDF para o router React.
  //
  const handleSisiAction =
    async (
      actionPath
    ) => {

      if (
        !actionPath
      ) {
        return;
      }


      // ------------------------------------------------------
      // DOCUMENTO / ENDPOINT BACKEND
      // ------------------------------------------------------

      if (
        actionPath.startsWith(
          "/api/"
        )
      ) {

        try {

          /*
           * api já possui baseURL terminando em /api.
           *
           * Backend retorna:
           * /api/integrations/...
           *
           * Para evitar /api/api/..., retiramos somente
           * o prefixo /api.
           */

          const apiPath =
            actionPath.slice(
              4
            ) ||
            "/";


          const response =
            await api.get(
              apiPath,
              {
                responseType:
                  "blob",
              }
            );


          const contentType =
            response
              ?.headers
              ?.["content-type"] ||
            response
              ?.data
              ?.type ||
            "application/pdf";


          const blob =
            response.data instanceof Blob
              ? response.data
              : new Blob(
                  [
                    response.data
                  ],
                  {
                    type:
                      contentType,
                  }
                );


          const objectUrl =
            URL.createObjectURL(
              blob
            );


          window.open(
            objectUrl,
            "_blank",
            "noopener,noreferrer"
          );


          window.setTimeout(
            () => {
              URL.revokeObjectURL(
                objectUrl
              );
            },
            60000
          );


          return;

        } catch (
          error
        ) {

          console.error(
            "Sisi document action error:",
            {
              actionPath,
              status:
                error
                  ?.response
                  ?.status,
              detail:
                error
                  ?.response
                  ?.data
                  ?.detail,
            }
          );


          /*
           * Não navegar para uma página React inexistente.
           * Mantemos o chat aberto e o erro apenas no console.
           */

          return;
        }
      }


      // ------------------------------------------------------
      // PÁGINA NORMAL DO APP
      // ------------------------------------------------------

      navigate(
        actionPath
      );
    };



  // ========================================================================
  // SISI_UNIVERSAL_ACTION_HANDLER_V2
  // ========================================================================
  //
  // page     -> navegação React normal
  // document -> busca autenticada como Blob e abre
  // download -> busca autenticada como Blob e baixa
  //
  const handleSisiUniversalAction =
    async (
      action
    ) => {

      if (
        !action
      ) {
        return;
      }


      const path =
        String(
          action.path ||
          ""
        );


      if (
        !path
      ) {
        return;
      }


      const type =
        action.type ||
        (
          path.startsWith(
            "/api/"
          )
            ? "document"
            : "page"
        );


      // ------------------------------------------------------
      // TELA INTERNA DA SIMPLER
      // ------------------------------------------------------

      if (
        type === "page"
      ) {

        navigate(
          path
        );

        return;
      }


      // ------------------------------------------------------
      // DOCUMENTO REAL
      // ------------------------------------------------------

      try {

        /*
         * api.baseURL já termina em /api.
         *
         * Exemplo recebido:
         * /api/ai/sisi/document/cndt/...
         *
         * Axios deve receber:
         * /ai/sisi/document/cndt/...
         */

        const apiPath =
          path.startsWith(
            "/api/"
          )
            ? path.slice(
                4
              )
            : path;


        const response =
          await api.get(
            apiPath,
            {
              responseType:
                "blob",
            }
          );


        const contentType =
          response
            ?.headers
            ?.["content-type"] ||
          response
            ?.data
            ?.type ||
          "application/pdf";


        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob(
                [
                  response.data
                ],
                {
                  type:
                    contentType,
                }
              );


        const objectUrl =
          URL.createObjectURL(
            blob
          );


        if (
          type === "download"
        ) {

          const link =
            document.createElement(
              "a"
            );


          link.href =
            objectUrl;


          link.download =
            action.filename ||
            "documento.pdf";


          document.body.appendChild(
            link
          );


          link.click();


          link.remove();

        } else {

          window.open(
            objectUrl,
            "_blank",
            "noopener,noreferrer"
          );
        }


        window.setTimeout(
          () => {
            URL.revokeObjectURL(
              objectUrl
            );
          },
          60000
        );

      } catch (
        error
      ) {

        console.error(
          "Sisi document action error:",
          {
            path,
            type,

            status:
              error
                ?.response
                ?.status,

            detail:
              error
                ?.response
                ?.data
                ?.detail,
          }
        );
      }
    };


  const handleQuickAction =
    async (
      id
    ) => {
      if (
        id === "portfolio"
      ) {
        return handleSend(
          "Analise minha carteira"
        );
      }


      if (
        id === "attention"
      ) {
        return handleSend(
          "O que precisa da minha atenção?"
        );
      }


      if (
        id === "messages"
      ) {
        return handleSend(
          "Ver mensagens importantes"
        );
      }
    };


  if (
    !open
  ) {
    return null;
  }


  return (
    <>
      <ScreenOverlay><button
        type="button"
        aria-label="Fechar Sisi"
        onClick={
          onClose
        }
        className="fixed inset-0 z-[70] lg:hidden bg-black/40 backdrop-blur-sm"
      /></ScreenOverlay>


      <aside
        className="
          fixed bottom-5 right-5 z-[80]
          flex h-[min(720px,calc(100vh-40px))]
          w-[calc(100vw-32px)] max-w-[460px]
          flex-col overflow-hidden
          rounded-2xl border border-border
          bg-background shadow-2xl
        "
        data-testid="sisi-chat-panel"
      >
        <header
          className="relative overflow-hidden border-b border-border bg-primary px-5 py-4 text-primary-foreground"
        >
          <div
            className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/10"
          />

          <div
            className="relative flex items-center gap-3"
          >
            <SisiAvatar
              size="lg"
              online
            />

            <div
              className="min-w-0 flex-1"
            >
              <div
                className="truncate text-[15px] font-semibold"
              >
                Sisi
              </div>

              <div
                className="mt-0.5 text-[11px] text-primary-foreground/75"
              >
                Sua Analista Fiscal Inteligente
              </div>

              <div
                className="mt-1.5 flex items-center gap-1.5 text-[10px] font-medium text-primary-foreground/85"
              >
                <span
                  className="h-1.5 w-1.5 rounded-full bg-emerald-400"
                />

                {loadingContext
                  ? "Carregando dados..."
                  : "Online"}
              </div>
            </div>


            <div className="flex items-center gap-1">
              {messages.length > 1 && (
                <button
                  type="button"
                  onClick={
                    resetConversation
                  }
                  className="hidden rounded-lg px-2.5 py-2 text-[10px] font-semibold text-primary-foreground/75 transition hover:bg-white/10 hover:text-primary-foreground sm:inline-flex"
                >
                  Nova conversa
                </button>
              )}

              <button
                type="button"
                onClick={
                  onClose
                }
                className="flex h-9 w-9 items-center justify-center rounded-lg text-primary-foreground/70 transition hover:bg-white/10 hover:text-primary-foreground"
                aria-label="Fechar"
              >
                <X
                  className="h-4 w-4"
                />
              </button>
            </div>
          </div>
        </header>


        <div
          ref={
            scrollRef
          }
          className="flex-1 overflow-y-auto bg-muted/20 px-4 py-4"
        >
          <div
            className="space-y-4"
          >
            {messages.map(
              message => (
                <div
                  key={
                    message.id
                  }
                  className={[
                    "flex gap-2.5",
                    message.role ===
                    "user"
                      ? "justify-end"
                      : "items-start",
                  ].join(
                    " "
                  )}
                >
                  {message.role ===
                    "assistant" && (
                    <SisiAvatar
                      size="sm"
                    />
                  )}


                  <div
                    className={[
                      "max-w-[350px] whitespace-pre-wrap rounded-2xl px-4 py-3 text-[12px] leading-5 shadow-sm",
                      message.role ===
                      "user"
                        ? "rounded-tr-md bg-primary text-primary-foreground"
                        : "rounded-tl-md border border-border bg-card text-foreground",
                    ].join(
                      " "
                    )}
                  >
                    {message.role === "assistant" ? (<SisiMarkdown text={message.content} />) : (message.content)}


                    {Array.isArray(
                      message.candidates
                    ) &&
                      message.candidates.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {message.candidates.map(
                            candidate => (
                              <button
                                key={
                                  candidate.id
                                }
                                type="button"
                                onClick={() =>
                                  handleCandidateSelect(
                                    candidate,
                                    message.originalQuestion
                                  )
                                }
                                className="block w-full rounded-xl border border-border bg-background px-3 py-2.5 text-left transition hover:border-primary/30 hover:bg-primary/[0.03]"
                              >
                                <div className="text-[10px] font-semibold leading-4 text-foreground">
                                  {
                                    candidate.razao_social ||
                                    candidate.nome_fantasia ||
                                    "Empresa"
                                  }
                                </div>

                                {candidate.cnpj && (
                                  <div className="mt-1 text-[9px] text-muted-foreground">
                                    CNPJ: {
                                      candidate.cnpj
                                    }
                                  </div>
                                )}
                              </button>
                            )
                          )}
                        </div>
                      )}


                    {Array.isArray(
                      message.actions
                    ) &&
                    message.actions.length > 0 ? (
                      <div
                        className="mt-3 space-y-2"
                      >
                        {message.actions.map(
                          (
                            action,
                            actionIndex
                          ) => (
                            <button
                              key={
                                `${
                                  action.path ||
                                  "action"
                                }-${actionIndex}`
                              }
                              type="button"
                              onClick={() =>
                                handleSisiUniversalAction(
                                  action
                                )
                              }
                              className="flex w-full items-center justify-between rounded-lg border border-primary/15 bg-background px-3 py-2 text-[10px] font-semibold text-primary transition hover:border-primary/30 hover:bg-primary/[0.03]"
                            >
                              <span>
                                {
                                  action.label ||
                                  "Abrir documento"
                                }
                              </span>

                              <ArrowRight
                                className="h-3 w-3"
                              />
                            </button>
                          )
                        )}
                      </div>
                    ) : message.actionPath ? (
                      <button
                        type="button"
                        onClick={() =>
                          handleSisiUniversalAction(
                            {
                              type:
                                message
                                  .actionPath
                                  .startsWith(
                                    "/api/"
                                  )
                                  ? "document"
                                  : "page",

                              path:
                                message.actionPath,

                              label:
                                message.actionLabel,
                            }
                          )
                        }
                        className="mt-3 flex w-full items-center justify-between rounded-lg border border-primary/15 bg-background px-3 py-2 text-[10px] font-semibold text-primary"
                      >
                        {
                          message.actionLabel ||
                          "Abrir módulo"
                        }

                        <ArrowRight
                          className="h-3 w-3"
                        />
                      </button>
                    ) : null}

                  </div>
                </div>
              )
            )}


            {sending && (
              <div
                className="flex items-start gap-2.5"
              >
                <SisiAvatar
                  size="sm"
                />

                <div
                  className="flex items-center gap-2 rounded-2xl rounded-tl-md border border-border bg-card px-4 py-3 text-[11px] text-muted-foreground"
                >
                  <Loader2
                    className="h-3.5 w-3.5 animate-spin text-primary"
                  />

                  Analisando dados...
                </div>
              </div>
            )}
          </div>

          {messages.length > 1 && !sending && (
            <div className="mt-5">
              <div className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Continuar
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={
                    resetConversation
                  }
                  className="rounded-lg border border-border bg-card px-3 py-2.5 text-[10px] font-semibold text-foreground transition hover:border-primary/25 hover:text-primary"
                >
                  Nova análise
                </button>

                <button
                  type="button"
                  onClick={() =>
                    handleSend(
                      "O que precisa da minha atenção?"
                    )
                  }
                  className="rounded-lg border border-border bg-card px-3 py-2.5 text-[10px] font-semibold text-foreground transition hover:border-primary/25 hover:text-primary"
                >
                  Ver prioridades
                </button>

                <button
                  type="button"
                  onClick={() =>
                    handleSend(
                      "Mostrar empresas"
                    )
                  }
                  className="rounded-lg border border-border bg-card px-3 py-2.5 text-[10px] font-semibold text-foreground transition hover:border-primary/25 hover:text-primary"
                >
                  Listar empresas
                </button>
              </div>
            </div>
          )}


          {messages.length <= 1 && (
            <div
              className="mt-6"
            >
              <div
                className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
              >
                Atalhos
              </div>

              <div
                className="space-y-2"
              >
                {QUICK_ACTIONS.map(
                  action => {
                    const Icon =
                      action.icon;

                    return (
                      <button
                        key={
                          action.id
                        }
                        type="button"
                        disabled={
                          sending ||
                          loadingContext
                        }
                        onClick={() =>
                          handleQuickAction(
                            action.id
                          )
                        }
                        className="
                          group flex w-full items-center gap-3
                          rounded-xl border border-border bg-card
                          px-3.5 py-3 text-left
                          transition
                          hover:border-primary/30 hover:bg-primary/[0.03]
                          disabled:opacity-50
                        "
                      >
                        <div
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"
                        >
                          <Icon
                            className="h-4 w-4"
                          />
                        </div>

                        <div
                          className="min-w-0 flex-1"
                        >
                          <div
                            className="text-[12px] font-semibold text-foreground"
                          >
                            {
                              action.label
                            }
                          </div>

                          <div
                            className="mt-0.5 text-[10px] leading-relaxed text-muted-foreground"
                          >
                            {
                              action.description
                            }
                          </div>
                        </div>

                        <ArrowRight
                          className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary"
                        />
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          )}
        </div>


        <div
          className="border-t border-border bg-background p-3"
        >
          <form
            onSubmit={
              event => {
                event.preventDefault();
                handleSend();
              }
            }
            className="flex items-end gap-2"
          >
            <textarea
              value={
                input
              }
              onChange={
                event =>
                  setInput(
                    event.target.value
                  )
              }
              onKeyDown={
                event => {
                  if (
                    event.key ===
                      "Enter" &&
                    !event.shiftKey
                  ) {
                    event.preventDefault();
                    handleSend();
                  }
                }
              }
              rows={1}
              placeholder="Pergunte algo para a Sisi..."
              className="min-h-[42px] max-h-[110px] flex-1 resize-none rounded-xl border border-border bg-background px-3.5 py-3 text-[12px] outline-none transition placeholder:text-muted-foreground focus:border-primary/40 focus:ring-2 focus:ring-primary/10"
            />

            <button
              type="submit"
              disabled={
                sending ||
                !input.trim()
              }
              className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground transition hover:opacity-95 disabled:opacity-40"
              aria-label="Enviar"
            >
              {sending ? (
                <Loader2
                  className="h-4 w-4 animate-spin"
                />
              ) : (
                <Send
                  className="h-4 w-4"
                />
              )}
            </button>
          </form>


          <button
            type="button"
            onClick={() => {
              navigate(
                "/app/sisi"
              );

              onClose();
            }}
            className="mt-2 flex h-9 w-full items-center justify-center gap-2 rounded-lg text-[10px] font-semibold text-muted-foreground transition hover:bg-muted hover:text-primary"
          >
            <BarChart3
              className="h-3.5 w-3.5"
            />

            Abrir central da Sisi
          </button>
        </div>
      </aside>
    </>
  );
}
